// src/pages/Admin/Users/UserDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  User as UserIcon,
  Mail,
  Calendar,
  Shield,
  ShieldOff,
  CheckCircle,
  XCircle,
  Trash2,
  AlertTriangle
} from 'lucide-react';
import adminService, { type User } from '../../../services/adminService';
import { useToast } from '../../../hooks/useToast';
import ConfirmDialog from '../../../components/Admin/ConfirmDialog';

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    action: () => void;
    danger?: boolean;
  }>({ isOpen: false, title: '', message: '', action: () => {}, danger: false });

  const loadUser = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const user = await adminService.getUserById(id);
      setUser(user);
    } catch (_err) {
      console.error('Error loading user:', _err);
      setError('Failed to load user details');
    } finally {
      setLoading(false);
    }
  };  useEffect(() => {
    if (id) {
      loadUser();
    } else {
      setError('No user ID provided');
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleToggleAdmin = () => {
    if (!user) return;
    
    if (user.isStaff || user.isAdminUser) {
      setConfirmDialog({
        isOpen: true,
        title: 'Remove Admin Role',
        message: `Are you sure you want to remove admin privileges from ${user.firstName} ${user.lastName}?`,
        action: () => performRemoveAdmin(),
        danger: true
      });
    } else {
      setConfirmDialog({
        isOpen: true,
        title: 'Grant Admin Role',
        message: `Are you sure you want to grant admin privileges to ${user.firstName} ${user.lastName}?`,
        action: () => performMakeAdmin()
      });
    }
  };

  const handleDeleteUser = () => {
    if (!user) return;
    
    setConfirmDialog({
      isOpen: true,
      title: 'Delete User',
      message: `Are you sure you want to permanently delete ${user.firstName} ${user.lastName}? This action cannot be undone.`,
      action: () => performDeleteUser(),
      danger: true
    });
  };

  const performMakeAdmin = async () => {
    if (!user) return;
    
    try {
      setActionLoading(true);
      await adminService.makeUserAdmin(user.id);
      showToast('User granted admin privileges', 'success');
      loadUser(); // Reload user data
    } catch (_err) {
      showToast('Failed to grant admin privileges', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const performRemoveAdmin = async () => {
    if (!user) return;
    
    try {
      setActionLoading(true);
      await adminService.removeUserAdmin(user.id);
      showToast('Admin privileges removed', 'success');
      loadUser(); // Reload user data
    } catch (_err) {
      showToast('Failed to remove admin privileges', 'error');
    } finally {
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  const performDeleteUser = async () => {
    if (!user) return;
    
    try {
      setActionLoading(true);
      await adminService.deleteUser(user.id);
      showToast('User deleted successfully', 'success');
      navigate('/admin/users'); // Navigate back to users list
    } catch (_err) {
      showToast('Failed to delete user', 'error');
      setActionLoading(false);
      setConfirmDialog({ ...confirmDialog, isOpen: false });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading User</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={() => navigate('/admin/users')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </button>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">User Not Found</h3>
        <p className="text-gray-600 mb-4">The user you're looking for doesn't exist.</p>
        <button
          onClick={() => navigate('/admin/users')}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/admin/users')}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Users
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
            <p className="mt-1 text-sm text-gray-600">
              View and manage user information
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleToggleAdmin}
            className={`inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white ${
              (user.isStaff || user.isAdminUser) 
                ? 'bg-orange-600 hover:bg-orange-700' 
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {(user.isStaff || user.isAdminUser) ? (
              <>
                <ShieldOff className="h-4 w-4 mr-2" />
                Remove Admin
              </>
            ) : (
              <>
                <Shield className="h-4 w-4 mr-2" />
                Make Admin
              </>
            )}
          </button>
          
          <button
            onClick={handleDeleteUser}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete User
          </button>
        </div>
      </div>

      {/* User Profile Card */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-8">
          <div className="flex items-center">
            <div className="h-20 w-20 rounded-full bg-gradient-to-r from-orange-400 to-amber-400 flex items-center justify-center text-white text-2xl font-bold">
              {user.firstName ? user.firstName[0].toUpperCase() : 'U'}
            </div>
            <div className="ml-6">
              <h2 className="text-2xl font-bold text-gray-900">
                {user.firstName} {user.lastName}
              </h2>
              <p className="text-lg text-gray-600">@{user.username}</p>
              <div className="mt-2 flex items-center space-x-4">
                {/* Verification Status */}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.isVerified
                    ? 'bg-green-100 text-green-800'
                    : 'bg-yellow-100 text-yellow-800'
                }`}>
                  {user.isVerified ? (
                    <>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-3 w-3 mr-1" />
                      Pending
                    </>
                  )}
                </span>
                
                {/* Active Status */}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  user.isActive
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                }`}>
                  {user.isActive ? (
                    <>
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Active
                    </>
                  ) : (
                    <>
                      <XCircle className="h-3 w-3 mr-1" />
                      Inactive
                    </>
                  )}
                </span>
                
                {/* Role */}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  (user.isStaff || user.isAdminUser)
                    ? 'bg-orange-100 text-orange-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {(user.isStaff || user.isAdminUser) ? (
                    <>
                      <Shield className="h-3 w-3 mr-1" />
                      Admin
                    </>
                  ) : (
                    'User'
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* User Details */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">User Information</h3>
        </div>
        <div className="px-6 py-4">
          <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Mail className="h-4 w-4 mr-2" />
                Email
              </dt>
              <dd className="mt-1 text-sm text-gray-900">{user.email}</dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <UserIcon className="h-4 w-4 mr-2" />
                Username
              </dt>
              <dd className="mt-1 text-sm text-gray-900">@{user.username}</dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Date Joined
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {user.dateJoined ? new Date(user.dateJoined).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'N/A'}
              </dd>
            </div>
            
            <div>
              <dt className="text-sm font-medium text-gray-500 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                Last Login
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                }) : 'Never'}
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={confirmDialog.action}
        title={confirmDialog.title}
        message={confirmDialog.message}
        danger={confirmDialog.danger}
        loading={actionLoading}
      />
    </div>
  );
};

export default UserDetail;